import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button } from 'antd';
import s from './s.module.less';

type Props = {
    open: boolean;
    // the control the spotlight cuts out — "Collect patient reviews"
    targetRef: React.RefObject<HTMLElement>;
    onDismiss: () => void;
    onShowMe: () => void;
};

type TPos = {
    left: number;
    top: number;
    width: number;
    height: number;
    tipTop: number;
    tipLeft: number;
    below: boolean;
    tailX: number;
};

const PAD = 6; // breathing room around the highlighted control
const TIP_GAP = 14; // distance from the hole to the tip, leaves room for the tail
const TIP_W = 320;

const ReviewLinkCoachmark: React.FC<Props> = ({ open, targetRef, onDismiss, onShowMe }) => {
    const tipRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<TPos>();

    const position = useCallback(() => {
        const target = targetRef.current;
        if (!target) {
            return;
        }
        const r = target.getBoundingClientRect();
        if (!r.width && !r.height) {
            return;
        }
        const tipH = tipRef.current?.offsetHeight || 150;
        // prefer sitting below the control, flip above when there isn't room
        const below = r.bottom + PAD + TIP_GAP + tipH < window.innerHeight;
        // right-align to the control, then keep the tail over the control's centre
        const tipLeft = Math.max(16, Math.min(r.right - TIP_W, window.innerWidth - TIP_W - 16));
        const tailX = Math.min(Math.max(r.left + r.width / 2 - tipLeft - 11, 14), TIP_W - 36);
        setPos({
            left: r.left - PAD,
            top: r.top - PAD,
            width: r.width + PAD * 2,
            height: r.height + PAD * 2,
            tipTop: below ? r.bottom + PAD + TIP_GAP : r.top - PAD - TIP_GAP - tipH,
            tipLeft,
            below,
            tailX,
        });
    }, [targetRef]);

    useLayoutEffect(() => {
        if (!open) {
            setPos(undefined);
            return undefined;
        }
        position();
        // the header buttons reflow once webfonts land, so the first measurement
        // can be stale — re-measure after layout has settled
        const raf = requestAnimationFrame(position);
        // `true` so scrolling any ancestor (the dashboard scrolls inside #siteLayout)
        // drags the spotlight along with the button
        window.addEventListener('scroll', position, true);
        window.addEventListener('resize', position);
        let ro: ResizeObserver | undefined;
        if (window.ResizeObserver && targetRef.current) {
            ro = new ResizeObserver(position);
            ro.observe(targetRef.current);
            ro.observe(document.body);
        }
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('scroll', position, true);
            window.removeEventListener('resize', position);
            ro?.disconnect();
        };
    }, [open, position, targetRef]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onDismiss();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onDismiss]);

    if (!open || !pos) {
        return null;
    }

    // clicking the dimmed area dismisses; clicking inside the spotlight hits the
    // real button underneath, so the highlight isn't just a picture of a button
    const handleScrimClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target !== e.currentTarget) {
            return;
        }
        const inHole =
            e.clientX >= pos.left &&
            e.clientX <= pos.left + pos.width &&
            e.clientY >= pos.top &&
            e.clientY <= pos.top + pos.height;
        onDismiss();
        if (inHole) {
            targetRef.current?.click();
        }
    };

    return ReactDOM.createPortal(
        <div className={s.tour} onClick={handleScrimClick}>
            <div
                className={s.hole}
                style={{ left: pos.left, top: pos.top, width: pos.width, height: pos.height }}
            />
            <div
                ref={tipRef}
                className={`${s.tip} ${pos.below ? s.tipBelow : s.tipAbove}`}
                style={{ top: pos.tipTop, left: pos.tipLeft, ['--tail-x' as string]: `${pos.tailX}px` }}
            >
                <div className={s.tipText}>Copy your review link and send it to patients to collect reviews.</div>
                <div className={s.tipActs}>
                    <Button className={s.tipBtn} onClick={onDismiss}>
                        Dismiss
                    </Button>
                    <Button type="primary" className={s.tipBtn} onClick={onShowMe}>
                        Show me how it works
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default ReviewLinkCoachmark;
